import { convert, sameDimension, type Amount } from './quantity.ts';

/** What combining needs of an ingredient line. */
export interface CombinableLine extends Amount {
  id: string;
  name: string;
  ingredientId: string | null;
  groupLabel: string | null;
  grams: number | null;
  note: string | null;
  optional: boolean;
}

const fold = (text: string) => text.trim().toLowerCase().normalize('NFC');

/** Adds `line` into `into` when their amounts can be summed; false when they can't. */
function merge<T extends CombinableLine>(into: T, line: T): T | null {
  const note = into.note === line.note ? into.note : null;
  const base = {
    ...into,
    groupLabel: null,
    note,
    optional: into.optional && line.optional,
    grams: into.grams !== null && line.grams !== null ? into.grams + line.grams : null,
  };
  if (into.quantity === null && line.quantity === null) {
    return into.unit === line.unit ? base : null;
  }
  if (into.quantity === null || line.quantity === null) return null;
  if (!sameDimension(into.unit, line.unit)) return null;
  const toTarget = (q: number) =>
    into.unit && line.unit ? convert(q, line.unit, into.unit) : into.unit === line.unit ? q : null;
  const quantity = toTarget(line.quantity);
  if (quantity === null) return null;
  const hasMax = (into.quantityMax ?? null) !== null || (line.quantityMax ?? null) !== null;
  const lineMax = toTarget(line.quantityMax ?? line.quantity) ?? quantity;
  return {
    ...base,
    quantity: into.quantity + quantity,
    quantityMax: hasMax ? (into.quantityMax ?? into.quantity) + lineMax : null,
  };
}

/**
 * The overall ingredient list of a recipe whose lines are split into components ("Für die
 * Sauce", "Für das Hähnchen"): the same ingredient from several components becomes one line with
 * the amounts added up (200 g + 0,1 kg → 300 g), in the order of first appearance. Amounts that
 * don't add up (2 Stück + 1 Dose) stay separate lines.
 */
export function combineLines<T extends CombinableLine>(lines: readonly T[]): T[] {
  const out: T[] = [];
  for (const line of lines) {
    const key = line.ingredientId ?? fold(line.name);
    let merged = false;
    for (const [i, existing] of out.entries()) {
      if ((existing.ingredientId ?? fold(existing.name)) !== key) continue;
      const sum = merge(existing, line);
      if (sum) {
        out[i] = sum;
        merged = true;
        break;
      }
    }
    if (!merged) out.push({ ...line, groupLabel: null });
  }
  return out;
}

/** The distinct component labels of a recipe's lines, in order. */
export function componentsOf(lines: readonly { groupLabel: string | null }[]): string[] {
  return [...new Set(lines.map((l) => l.groupLabel).filter((g): g is string => g !== null))];
}
